import {inject} from 'vue';
import type {BuildQueryURLArgs, Query} from '@prismicio/client';
import type {PrismicDocument} from '@prismicio/types';
import {callWithNuxt, useNuxtApp} from '#app';
import {useLocale, useLogger, usePrismicLang, usePrismicRedirect, useRoute, useSpanWrap} from '#imports';
import {safelyNavigateTo} from '@malt/utils';
import {PRISMIC_CLIENT} from '~/keys';

type AlternateLanguage = {
    id: string;
    type: string;
    lang: string;
    uid?: string;
};

export type PrismicDocumentOrRedirect = PrismicDocument | Awaited<ReturnType<typeof safelyNavigateTo>>;

/**
 * Retrieve document from Prismic repository.
 * @param documentType  Document type.
 * @param uid           Document UID.
 */
export function usePrismicDocument(documentType: string, uid?: string) {
    const nuxtApp = useNuxtApp();
    const logger = useLogger();
    const client = inject(PRISMIC_CLIENT);
    const route = useRoute();
    const {locale} = useLocale();

    const {lang} = usePrismicLang();

    const {fetchRedirect} = usePrismicRedirect(route.path, locale.value);

    function isPrismicDocument(value: unknown): value is PrismicDocument {
        return typeof value === 'object' && value !== null && 'id' in value && 'href' in value && 'data' in value;
    }

    async function fetchDocument(params?: {fetchLinks?: string[]}): Promise<PrismicDocument> {
        try {
            const getPrismicDocument = () =>
                uid
                    ? client!.getByUID(documentType, uid, {lang: lang.value, ...params})
                    : client!.getSingle(documentType, {lang: lang.value, ...params});
            return await useSpanWrap(getPrismicDocument, {httpMethod: 'prismic.get', kind: 'prismic', name: `[prismic] ${documentType}`});
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : err;
            logger.info(
                `Could not retrieve document from Prismic with parameters: [${documentType}, ${locale.value}, ${uid}]. Error message=${message}. Trying redirection...`,
            );
        }
        // If the document does not exist, check for redirect or throw 404
        return await callWithNuxt(nuxtApp, fetchRedirect);
    }

    async function fetchDocuments(params?: Partial<BuildQueryURLArgs>): Promise<Query | undefined> {
        try {
            return await useSpanWrap(() => client!.getByType(documentType, {lang: lang.value, ...params}), {
                httpMethod: 'prismic.get',
                kind: 'prismic',
                name: `[prismic] ${documentType}`,
            });
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : err;
            logger.info(`Could not retrieve documents from Prismic with parameters: [${documentType}, ${locale.value}]. Error message=${message}.`);
        }

        return undefined;
    }

    async function fetchDocumentWithFallback(params?: {fetchLinks?: string[]}): Promise<PrismicDocumentOrRedirect> {
        try {
            const getPrismicDocument = () =>
                uid
                    ? client!.getByUID(documentType, uid, {lang: lang.value, ...params})
                    : client!.getSingle(documentType, {lang: lang.value, ...params});
            return await useSpanWrap(getPrismicDocument, {httpMethod: 'prismic.get', kind: 'prismic', name: `[prismic] ${documentType}`});
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : err;
            logger.info(
                `Could not retrieve document from Prismic with parameters: [${documentType}, ${locale.value}, ${uid}]. Error message=${message}. Trying redirection...`,
            );
        }

        // If the document does not exist, try to fallback to an alternate language (via uid only)
        try {
            if (uid) {
                const maybeFallback = await _fallbackToAlternateLanguage(documentType, uid);
                const navigation = await _maybeSafelyNavigateToAlternate(uid, maybeFallback);
                if (navigation) {
                    return navigation;
                }
            }
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : err;
            logger.info(
                `Could not retrieve document from Prismic with parameters: [${documentType}, ${locale.value}, ${uid}]. Error message=${message}. Trying redirection...`,
            );
        }

        // If the document does not exist and no alternate language is found, check for redirect or throw 404
        return await callWithNuxt(nuxtApp, fetchRedirect);
    }

    async function _fallbackToAlternateLanguage(documentType: string, uid: string): Promise<AlternateLanguage | undefined> {
        const getPrismicDocument = () => client!.getAllByUIDs(documentType, [uid!], {lang: '*'});
        const result = await callWithNuxt(nuxtApp, () => {
            return useSpanWrap(getPrismicDocument, {httpMethod: 'prismic.get', kind: 'prismic', name: `[prismic] ${documentType}`});
        });

        if (result.length > 0 && result[0].alternate_languages && result[0].alternate_languages.length > 0) {
            const maybeFallback: AlternateLanguage | undefined = result[0].alternate_languages.find(
                (language: AlternateLanguage) => language.lang === lang.value,
            );
            return maybeFallback;
        }
    }

    async function _maybeSafelyNavigateToAlternate(
        currentUid: string,
        maybeFallback: AlternateLanguage | undefined,
    ): Promise<Awaited<ReturnType<typeof safelyNavigateTo>> | undefined> {
        if (!maybeFallback?.uid) {
            return undefined;
        }

        const segments = route.path.split('/');
        const lastSegmentIndex = segments.length - 1;
        if (segments[lastSegmentIndex] !== currentUid) {
            return undefined;
        }

        segments[lastSegmentIndex] = maybeFallback.uid;
        const newRoute = segments.join('/');
        if (newRoute === route.path) {
            return undefined;
        }

        return await callWithNuxt(nuxtApp, () => safelyNavigateTo({path: newRoute, query: route.query, hash: route.hash}, {redirectCode: 301}));
    }

    return {
        fetchDocument,
        fetchDocuments,
        fetchDocumentWithFallback,
        isPrismicDocument,
    };
}
